import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { changePassword } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_admin/change-password')({
  component: ChangePasswordPage,
});

function isStrongPassword(pw: string): boolean {
  return (
    pw.length >= 8 &&
    /[A-Z]/.test(pw) &&
    /[a-z]/.test(pw) &&
    /[0-9]/.test(pw)
  );
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('As senhas nao coincidem');
      return;
    }
    if (!isStrongPassword(newPassword)) {
      toast.error('A senha nao atende os requisitos minimos');
      return;
    }
    setLoading(true);
    try {
      const res = await changePassword({ currentPassword, newPassword });
      if (!res.success) {
        toast.error(res.message || 'Erro ao alterar senha');
        return;
      }
      toast.success('Senha alterada com sucesso!');
      navigate({ to: '/meu-perfil' });
    } catch {
      toast.error('Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-3xl font-bold">Alterar senha</h1>
        <p className="text-muted-foreground">
          Informe sua senha atual e defina uma nova senha segura.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-card p-6">
        <div>
          <label htmlFor="current" className="mb-1.5 block text-sm font-medium">Senha atual</label>
          <input
            id="current"
            type="password"
            required
            autoFocus
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          />
        </div>
        <div>
          <label htmlFor="new" className="mb-1.5 block text-sm font-medium">Nova senha</label>
          <input
            id="new"
            type="password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Mínimo 8 caracteres, com maiúscula, minúscula e número.
          </p>
        </div>
        <div>
          <label htmlFor="confirm" className="mb-1.5 block text-sm font-medium">Confirmar nova senha</label>
          <input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="mt-1 text-xs text-destructive">As senhas nao coincidem</p>
          )}
        </div>
        <button
          type="submit"
          disabled={
            loading ||
            !currentPassword ||
            !isStrongPassword(newPassword) ||
            newPassword !== confirmPassword
          }
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {loading ? 'Alterando...' : 'Alterar senha'}
        </button>
      </form>
    </div>
  );
}
